import { lazy, Suspense, useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { Spinner } from "@heroui/spinner";

import IndexPage from "@/pages/index";
import { fetchAnalyticsConfig } from "@/api/analytics";
import OnboardingGate from "@/components/OnboardingGate";
import ProtectedRoute from "@/components/ProtectedRoute";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import {
  initAnalytics,
  setAdsConversionConfig,
  setAnalyticsMeasurementId,
  trackPageView,
} from "@/utils/analytics";
import {
  lazyRouteImporters,
  preloadLikelyRoutes,
  preloadRouteByPath,
} from "@/utils/routePrefetch";
import { frontendRoutes } from "@/config/frontendRoutes";
import { useAuth } from "@/state/AuthContext";

const TutorsPage = lazy(lazyRouteImporters.tutors);
const TutorsBySlugPage = lazy(lazyRouteImporters.tutorsBySlug);
const TutorsBySubjectCityPage = lazy(lazyRouteImporters.tutorsBySubjectCity);
const TutorDetailPage = lazy(lazyRouteImporters.tutorDetail);
const ConnexionPage = lazy(lazyRouteImporters.connexion);
const InscriptionPage = lazy(lazyRouteImporters.inscription);
const VerificationEmailPage = lazy(lazyRouteImporters.verificationEmail);
const ForgotPasswordPage = lazy(lazyRouteImporters.forgotPassword);
const ResetPasswordPage = lazy(lazyRouteImporters.resetPassword);
const DashboardPage = lazy(lazyRouteImporters.dashboard);
const ListingEditorPage = lazy(lazyRouteImporters.listingEditor);
const OnboardingPage = lazy(lazyRouteImporters.onboarding);
const ContactPage = lazy(lazyRouteImporters.contact);
const DemandePage = lazy(lazyRouteImporters.demande);
const PublicProfilePage = lazy(lazyRouteImporters.publicProfile);
const DeconnexionPage = lazy(lazyRouteImporters.deconnexion);
const AdminPage = lazy(lazyRouteImporters.admin);
const BlogPage = lazy(lazyRouteImporters.blog);
const BlogPostPage = lazy(lazyRouteImporters.blogPost);
const PressReleasesPage = lazy(lazyRouteImporters.pressReleases);
const PressReleasePostPage = lazy(lazyRouteImporters.pressReleasePost);
const MentionsLegalesPage = lazy(lazyRouteImporters.mentionsLegales);
const CguPage = lazy(lazyRouteImporters.cgu);
const DevenirProfesseurPage = lazy(lazyRouteImporters.devenirProfesseur);
const DevenirProfesseurFeaturePage = lazy(
  lazyRouteImporters.devenirProfesseurFeature,
);
const DiscoverProfLinkerPage = lazy(lazyRouteImporters.discoverProfLinker);
const ProfLinkerPage = lazy(lazyRouteImporters.profLinker);
const CourseSubjectPage = lazy(lazyRouteImporters.courseSubject);
const LateCancellationConfirmPage = lazy(() =>
  import("@/pages/LateCancellationConfirmPage").then((m) => ({
    default: m.default,
  })),
);
const LateCancellationContestPage = lazy(() =>
  import("@/pages/LateCancellationContestPage").then((m) => ({
    default: m.default,
  })),
);

// Max time the visible route is held on the previous page while the next
// route's chunk downloads. Past this, we swap anyway (Suspense takes over),
// so a navigation never looks frozen.
const ROUTE_SWAP_CAP_MS = 250;

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center">
      <Spinner color="primary" size="lg" />
    </div>
  );
}

function App() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [renderLocation, setRenderLocation] = useState(location);
  // Starts false so the very first client render matches the SSR markup
  // (fallback=null). Flipped after mount so subsequent client navigations can
  // show a loader instead of freezing on the old page.
  const [hydrated, setHydrated] = useState(false);
  const locationUrl = `${location.pathname}${location.search}${location.hash}`;
  const renderLocationUrl = `${renderLocation.pathname}${renderLocation.search}${renderLocation.hash}`;

  useEffect(() => {
    let isCancelled = false;

    fetchAnalyticsConfig()
      .then((config) => {
        if (isCancelled) return;
        setAdsConversionConfig({
          conversionId: config.adsConversionId,
          leadLabel: config.adsLeadConversionLabel,
        });
        setAnalyticsMeasurementId(config.measurementId);
        initAnalytics();
        const path = `${window.location.pathname}${window.location.search}`;

        trackPageView(path);
      })
      .catch(() => {
        // Keep analytics optional if backend config is unavailable.
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;

    trackPageView(path);
  }, [location.pathname, location.search]);

  // Reset scroll on route change (pathname). Don't fire when only the
  // hash changes — those are intra-page anchors and the browser handles
  // them. Don't fire on search-only changes either (keeps filter state).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [location.pathname]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const warm = () => preloadLikelyRoutes({ isAuthenticated });

    if ("requestIdleCallback" in window) {
      const idle = (window as any).requestIdleCallback(warm, { timeout: 1500 });

      return () => (window as any).cancelIdleCallback?.(idle);
    }
    timeoutId = globalThis.setTimeout(warm, 600);

    return () => {
      if (timeoutId) globalThis.clearTimeout(timeoutId);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    let isActive = true;

    if (locationUrl === renderLocationUrl) {
      if (renderLocation !== location) {
        setRenderLocation(location);
      }

      return;
    }

    const preloadPromise =
      preloadRouteByPath(locationUrl, {
        mode: "navigation",
        isAuthenticated,
      }) ?? Promise.resolve();

    // Swap when the preload finishes OR the cap elapses — whichever comes
    // first — so a slow chunk can't leave the previous page frozen (which
    // reads as "the button did nothing" and makes users click again).
    let capTimer: ReturnType<typeof setTimeout> | undefined;
    const cap = new Promise<void>((resolve) => {
      capTimer = setTimeout(resolve, ROUTE_SWAP_CAP_MS);
    });

    void Promise.race([preloadPromise, cap]).finally(() => {
      clearTimeout(capTimer);
      if (!isActive) return;
      setRenderLocation(location);
    });

    return () => {
      isActive = false;
      clearTimeout(capTimer);
    };
  }, [
    isAuthenticated,
    location,
    locationUrl,
    renderLocation,
    renderLocationUrl,
  ]);

  return (
    <RouteErrorBoundary resetKey={renderLocation.pathname}>
      <Suspense fallback={hydrated ? <RouteFallback /> : null}>
        <OnboardingGate location={renderLocation}>
          <Routes location={renderLocation}>
            <Route element={<IndexPage />} path={frontendRoutes.home} />
            <Route element={<TutorsPage />} path={frontendRoutes.tutors} />
            <Route
              element={<TutorsBySubjectCityPage />}
              path={frontendRoutes.courseByCitySubject}
            />
            <Route
              element={<TutorDetailPage />}
              path={frontendRoutes.courseTutorDetail}
            />
            <Route
              element={<TutorsBySlugPage />}
              path={frontendRoutes.courseBySlug}
            />
            <Route
              element={<ConnexionPage />}
              path={frontendRoutes.connexion}
            />
            <Route
              element={<InscriptionPage />}
              path={frontendRoutes.inscription}
            />
            <Route
              element={<VerificationEmailPage />}
              path={frontendRoutes.verificationEmail}
            />
            <Route
              element={<ForgotPasswordPage />}
              path={frontendRoutes.forgotPassword}
            />
            <Route
              element={<ResetPasswordPage />}
              path={frontendRoutes.resetPassword}
            />
            <Route element={<ContactPage />} path={frontendRoutes.contact} />
            <Route element={<DemandePage />} path={frontendRoutes.demande} />
            <Route
              element={<MentionsLegalesPage />}
              path={frontendRoutes.mentionsLegales}
            />
            <Route element={<CguPage />} path={frontendRoutes.cgu} />
            <Route element={<BlogPage />} path={frontendRoutes.blog} />
            <Route element={<BlogPostPage />} path={frontendRoutes.blogPost} />
            <Route
              element={<PressReleasesPage />}
              path={frontendRoutes.pressReleases}
            />
            <Route
              element={<PressReleasePostPage />}
              path={frontendRoutes.pressReleasePost}
            />
            <Route
              element={<DevenirProfesseurPage />}
              path={frontendRoutes.devenirProfesseur}
            />
            <Route
              element={<DevenirProfesseurFeaturePage />}
              path={frontendRoutes.devenirProfesseurFeature}
            />
            <Route
              element={<DiscoverProfLinkerPage />}
              path={frontendRoutes.discoverProfLinker}
            />
            <Route
              element={<ProfLinkerPage />}
              path={frontendRoutes.profLinker}
            />
            <Route
              element={<CourseSubjectPage subject="nsi" />}
              path={frontendRoutes.courseParticuliersNsi}
            />
            <Route
              element={<CourseSubjectPage subject="mathematiques" />}
              path={frontendRoutes.courseParticuliersMaths}
            />
            <Route
              element={<CourseSubjectPage subject="physique-chimie" />}
              path={frontendRoutes.courseParticuliersPhysics}
            />
            <Route
              element={<CourseSubjectPage />}
              path={frontendRoutes.courseParticuliersSubject}
            />
            <Route
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
              path={frontendRoutes.dashboard}
            />
            <Route
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
              path={`${frontendRoutes.dashboard}/:section`}
            />
            <Route
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
              path={`${frontendRoutes.dashboard}/:section/:subSection`}
            />
            <Route
              element={
                <ProtectedRoute>
                  <ListingEditorPage />
                </ProtectedRoute>
              }
              path={frontendRoutes.dashboardListingNew}
            />
            <Route
              element={
                <ProtectedRoute>
                  <ListingEditorPage />
                </ProtectedRoute>
              }
              path={frontendRoutes.dashboardListingEdit}
            />
            <Route
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
              path={frontendRoutes.onboarding}
            />
            <Route
              element={<PublicProfilePage />}
              path={frontendRoutes.publicProfile}
            />
            <Route
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              }
              path={frontendRoutes.admin}
            />
            <Route
              element={<DeconnexionPage />}
              path={frontendRoutes.deconnexion}
            />
            <Route
              element={<LateCancellationConfirmPage />}
              path={frontendRoutes.lateCancellationConfirm}
            />
            <Route
              element={<LateCancellationContestPage />}
              path={frontendRoutes.lateCancellationContest}
            />
          </Routes>
        </OnboardingGate>
      </Suspense>
    </RouteErrorBoundary>
  );
}

export default App;
